import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search, Eye } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaInput from "@/components/jea/JeaInput";
import Skeleton from "@/components/jea/Skeleton";
import EmptyState from "@/components/jea/EmptyState";
import UserDetailDrawer from "@/components/admin/UserDetailDrawer";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const STATUS_FILTERS = [
  { key: "all", label: "All Statuses" },
  { key: "active", label: "Active" },
  { key: "canceling", label: "Canceling" },
  { key: "past_due", label: "Past Due" },
  { key: "canceled", label: "Canceled" },
];
const PLAN_FILTERS = [
  { key: "all", label: "All Plans" },
  { key: "standard", label: "Standard" },
  { key: "pro", label: "PRO" },
];

const StatusPill = ({ status }) => {
  const map = {
    active: "bg-success/15 text-success border-success/40",
    canceling: "bg-warning/15 text-warning border-warning/40",
    past_due: "bg-danger/15 text-danger border-danger/40",
    canceled: "bg-bg-tertiary text-text-muted border-border-default",
  };
  return (
    <span className={cn("inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border", map[status] || map.active)}>
      {status || "—"}
    </span>
  );
};

const StatCard = ({ label, value, valueTone = "text-accent-pink" }) => (
  <div className="rounded-[12px] bg-bg-secondary border border-border-default p-5">
    <div className="jea-caption">{label}</div>
    <div className={cn("jea-mono font-extrabold text-[28px] mt-2", valueTone)}>{value}</div>
  </div>
);

const AdminSubscriptionsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [plan, setPlan] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (status !== "all") params.set("status", status);
      if (plan !== "all") params.set("plan", plan);
      params.set("limit", "50");
      const { data } = await apiClient.get(`/admin/subscriptions?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, plan]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const subs = data?.subscriptions || [];
  const summary = data?.summary || {};

  return (
    <div className="space-y-5" data-testid="admin-subscriptions-page">
      <div className="space-y-1">
        <div className="jea-caption">Recurring Revenue</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Subscriptions</h1>
        <p className="text-text-secondary text-[14px]">
          Read-only view. Users manage their own subscriptions from their Billing page.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-[12px]" />)
        ) : (
          <>
            <StatCard label="MRR" value={centsToUSD(summary.mrr_cents || 0)} />
            <StatCard label="ARR" value={centsToUSD(summary.arr_cents || 0)} />
            <StatCard label="Active" value={summary.active_subscriptions || 0} valueTone="text-white" />
            <StatCard label="Churn %" value={`${summary.churn_pct || 0}%`} valueTone="text-warning" />
          </>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="w-[320px]">
          <JeaInput
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search size={14} />}
            data-testid="subs-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatus(f.key)}
              className={cn(
                "h-9 px-3 rounded-full border text-[12px] font-semibold transition-all",
                status === f.key
                  ? "bg-accent-pink text-[#091424] border-accent-pink"
                  : "bg-bg-secondary border-border-default text-text-secondary hover:text-white",
              )}
              data-testid={`subs-status-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {PLAN_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setPlan(f.key)}
              className={cn(
                "h-9 px-3 rounded-full border text-[12px] font-semibold transition-all",
                plan === f.key
                  ? "bg-accent-pink text-[#091424] border-accent-pink"
                  : "bg-bg-secondary border-border-default text-text-secondary hover:text-white",
              )}
              data-testid={`subs-plan-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <JeaCard className="p-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
        </JeaCard>
      ) : subs.length === 0 ? (
        <EmptyState icon={Search} title="No subscriptions match" testid="subs-empty" />
      ) : (
        <JeaCard className="p-0 overflow-hidden" data-testid="admin-subscriptions-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-default">
                  <th className="jea-caption text-text-secondary p-4 text-left">User</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Plan</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Type</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Judgments</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Price</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">MRR</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Status</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Started</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Renews / Cancels</th>
                  <th className="jea-caption text-text-secondary p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => (
                  <motion.tr
                    key={s.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18, delay: i * 0.02 }}
                    className="border-b border-border-default hover:bg-bg-tertiary transition-colors"
                    data-testid={`subs-row-${s.id}`}
                  >
                    <td className="p-4">
                      <div className="text-white font-semibold text-[13px]">{s.user_name || "—"}</div>
                      <div className="text-text-secondary text-[12px]">{s.user_email}</div>
                    </td>
                    <td className="p-4 text-text-secondary text-[13px]">
                      {s.plan_type === "pro" ? `PRO ${s.path || ""}`.trim() : `Standard · ${{ A: "3M", B: "6M", C: "12M" }[s.path] || s.path || "?"}`}
                    </td>
                    <td className="p-4">
                      <span className={cn(
                        "inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border",
                        s.plan_type === "pro"
                          ? "bg-accent-pink/15 text-accent-pink border-accent-pink/40"
                          : "bg-bg-tertiary text-text-secondary border-border-default"
                      )}>
                        {(s.plan_type || "—").toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 text-text-secondary text-[13px] jea-mono">{s.debtor_count}</td>
                    <td className="p-4 jea-mono text-accent-pink font-bold text-[13px]">{centsToUSD(s.price_cents)}</td>
                    <td className="p-4 jea-mono text-white text-[13px]">{centsToUSD(s.mrr_cents)}</td>
                    <td className="p-4"><StatusPill status={s.status} /></td>
                    <td className="p-4 jea-date text-[13px]">{shortDate(s.started_at)}</td>
                    <td className="p-4 jea-date text-[13px]">
                      {s.status === "canceling" ? shortDate(s.cancels_at) : shortDate(s.renews_at)}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        type="button"
                        onClick={() => setSelectedId(s.user_id)}
                        className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/50 transition-colors inline-flex items-center justify-center"
                        title="View user"
                        data-testid={`subs-view-${s.id}`}
                      >
                        <Eye size={13} />
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </JeaCard>
      )}

      <UserDetailDrawer
        userId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onAction={() => setSelectedId(null)}
      />
    </div>
  );
};

export default AdminSubscriptionsPage;
